import * as React from "react";
import PropTypes from "prop-types";
import {
  TableRow,
  TableCell,
  useTheme,
  Tooltip,
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EditIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "../../assets/svg/DeleteIcon";
import IconButton from "@mui/material/IconButton";
import { createStyles } from "@mui/styles";
import { useProfiles } from "../../contexts/ProfilesContext";
import { useData } from "../../contexts/DataContext";
import { PDFDownloadLink } from "@react-pdf/renderer";
import Invoice from "../../sections/Invoicing/Invoice";
import {
  getTimesheetButtonText,
  TIMESHEET_STATUS
} from "../../utils/timesheetUtils";
import { accessibleFeatures } from "../../utils/rolesAccessMapping";
import { SelectInvoiceTemplate } from "../../sections/Invoicing/SelectInvoiceTemplate";

TableRowWithDelete.propTypes = {
  row: PropTypes.object,
  columns: PropTypes.array,
  onEditRow: PropTypes.func,
  onDeleteRow: PropTypes.func,
  onItemClick: PropTypes.func,
  timesheetPage: PropTypes.bool,
  invoicePage: PropTypes.bool,
  onRegenerateClick: PropTypes.func,
  onEmailClick: PropTypes.func,
  isActivityLogsPage: PropTypes.bool,
  showDelete: PropTypes.bool, // NEW PROP

  allowDelete: PropTypes.bool // NEW PROP
};

export default function TableRowWithDelete({
  row,
  columns,
  onEditRow,
  onDeleteRow,
  onItemClick,
  timesheetPage,
  invoicePage,
  weekWiseTimesheetPage,
  onRegenerateClick,
  onSubmitTimsheetClick,
  onEmailClick,
  onPaidClick,
  screen,
  MissingTimesheetsPage,
  isActivityLogsPage,
  isEmployeeOverview,
  showDelete = true,
  allowDelete
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const { userData } = useData();
  const profiles = useProfiles();
  const CellDataItem = ({ row, column }) => {
    const cellFormatValue =
      typeof column.format === "function"
        ? column.format(row)
        : row[column.id] || "--";
    const cellStyle = column.hasStyle ? column.style?.(cellFormatValue) : {};
    return <span style={cellStyle}>{cellFormatValue}</span>;
  };
  const isButtonDisabled = row.is_utilized;
  const isPriceButtonDisabled = row.is_price_utilized;
  const isContractButtonDisabled = row.is_contract_utilized_po;
  const buttonPermissions = {
    "Delete Client": accessibleFeatures.buttons.delete.deleteClient,
    "Delete Asset": accessibleFeatures.buttons.delete.deleteClient,
    "Delete Budget": accessibleFeatures.buttons.delete.deleteBudget,
    "Delete Estimation": accessibleFeatures.buttons.delete.deleteEstimation,
    "Delete Pricing": accessibleFeatures.buttons.delete.deletePricing,
    "Delete Contracts": accessibleFeatures.buttons.delete.deleteContracts,
    "Delete Allocation": accessibleFeatures.buttons.delete.deleteAllocation,
    "Delete Milestone": accessibleFeatures.buttons.delete.deleteMilestone,
    "Delete PO": accessibleFeatures.buttons.delete.deletePO,
    "Delete skillPayrate": accessibleFeatures.buttons.delete.deleteSkillPayrate,
    "Delete customer": accessibleFeatures.buttons.delete.deleteCustomer,
    "Delete Employee": accessibleFeatures.buttons.delete.deleteEmployee,
    "Delete Invoice Template":
      accessibleFeatures.buttons.delete.deleteInvoiceTemplate,
    "Delete Software": accessibleFeatures.buttons.delete.deleteEmployee
  };

  const renderDelBtn = userData?.user_roles?.some((role) =>
    buttonPermissions[`Delete ${screen}`]?.includes(role)
  ) ? (
    <Tooltip
      title={
        isButtonDisabled
          ? "Contract is locked for this Estimation, You can't delete it"
          : isPriceButtonDisabled
          ? "Contract is locked for this Pricing, You can't delete it"
          : isContractButtonDisabled
          ? "Contract is locked by PO, You can't delete it"
          : null
      }
      disableHoverListener={
        !isButtonDisabled && !isPriceButtonDisabled && !isContractButtonDisabled
      }
    >
      <span>
        {!timesheetPage && !invoicePage && (
          <IconButton
            color="default"
            aria-label="delete"
            size="small"
            onClick={() => onDeleteRow(row)}
            style={{
              // Show the button if user has permission OR allowDelete is true
              display:
                allowDelete ||
                userData?.user_roles?.some((role) =>
                  buttonPermissions[`Delete ${screen}`]?.includes(role)
                )
                  ? "inline-flex"
                  : "none",
              opacity:
                isButtonDisabled ||
                isPriceButtonDisabled ||
                isContractButtonDisabled
                  ? 0.3
                  : 1,
              pointerEvents:
                isButtonDisabled ||
                isPriceButtonDisabled ||
                isContractButtonDisabled
                  ? "none"
                  : "auto"
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </span>
    </Tooltip>
  ) : null;
  const isClientTab = localStorage.getItem("selectedTab") === "client";
  const [openTemplateModal, setOpenTemplateModal] = React.useState(false);

  const ActionButtons = () => (
    <>
      {(profiles.isInvoiceAdmin || profiles.isSuperAdmin) && (
        <>
          <Tooltip
            title={
              isClientTab
                ? "Generate Invoice with Amount"
                : "Generate Actuals with Amount"
            }
            placement="top"
            arrow
          >
            <IconButton
              disabled={row.c2c_invoice_status === "Paid"}
              color="default"
              aria-label="regenerate"
              size="small"
              onClick={() => onRegenerateClick(row)}
            >
              <CachedOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip
            title="Send Invoice to Client via Email"
            placement="top"
            arrow
          >
            <IconButton
              disabled={row.c2c_invoice_status === "Paid"}
              color="default"
              aria-label="email"
              size="small"
              onClick={() => onEmailClick(row)}
            >
              <EmailOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Mark Invoice as Paid" placement="top" arrow>
            <IconButton
              disabled={row.c2c_invoice_status === "Paid"}
              color="default"
              aria-label="check"
              size="small"
              onClick={() => onPaidClick(row)}
            >
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}

      {(profiles.isSuperAdmin ||
        profiles.isInvoiceAdmin ||
        profiles.isInvoiceViewer) && (
        <>
          {/* <Tooltip title="Download Invoice PDF" placement="top" arrow>
            <IconButton color="default" aria-label="download" size="small">
              <PDFDownloadLink
                fileName="invoice.pdf"
                document={<Invoice row={row} />}
              >
                <DownloadOutlinedIcon
                  style={{ color: "grey" }}
                  fontSize="small"
                />
              </PDFDownloadLink>
            </IconButton>
          </Tooltip> */}
          {/* New: Template Download Modal Trigger */}
          {invoicePage && (
            <Tooltip title="Download with Template" placement="top" arrow>
              <IconButton
                color="primary"
                aria-label="download-template"
                size="small"
                onClick={() => setOpenTemplateModal(true)}
              >
                <DownloadOutlinedIcon
                  style={{ color: "grey" }}
                  fontSize="small"
                />
              </IconButton>
            </Tooltip>
          )}
          {/* {(profiles.isSuperAdmin ||
            profiles.isInvoiceAdmin ||
            profiles.isInvoiceViewer) && (
            <Tooltip title="Download Invoice PDF" placement="top" arrow>
              <IconButton color="default" aria-label="download" size="small">
                <PDFDownloadLink
                  fileName="invoice.pdf"
                  document={<Invoice row={row} />}
                >
                  <DownloadOutlinedIcon
                    style={{ color: "grey" }}
                    fontSize="small"
                  />
                </PDFDownloadLink>
              </IconButton>
            </Tooltip>
          )} */}
          {/* Template Download Modal */}
          {openTemplateModal && (
            <Dialog
              open={openTemplateModal}
              onClose={() => setOpenTemplateModal(false)}
              maxWidth={false}
              PaperProps={{
                sx: {
                  width: "calc(100vw - 32px)",
                  height: "calc(100vh - 32px)",
                  margin: "16px",
                  borderRadius: "16px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column"
                }
              }}
              sx={{
                "& .MuiDialog-paper": {
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                  background: "white"
                }
              }}
            >
              <DialogTitle sx={{ position: "relative", paddingRight: "48px" }}>
                Select Invoice Template
                <IconButton
                  aria-label="close"
                  onClick={() => setOpenTemplateModal(false)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500]
                  }}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <SelectInvoiceTemplate
                  row={row}
                  invoiceRow={row}
                  onClose={() => setOpenTemplateModal(false)}
                />
              </DialogContent>
            </Dialog>
          )}
        </>
      )}
    </>
  );

  const TimesheetStatusButtons = ({ row }) => {
    const buttonText = getTimesheetButtonText(row);
    const isDisabled =
      buttonText === TIMESHEET_STATUS.approved_btn ||
      buttonText === TIMESHEET_STATUS.submitted_btn;
    let isRecalled;
    if (buttonText === TIMESHEET_STATUS.recall_btn) {
      isRecalled = true;
    }
    return (
      <>
        {weekWiseTimesheetPage && (
          <>
            {isEmployeeOverview ? (
              <Box> {buttonText}</Box>
            ) : (
              <Button
                onClick={() => onSubmitTimsheetClick(row, isRecalled)}
                variant="contained"
                disabled={isDisabled}
                sx={{ width: 160 }}
              >
                {buttonText}
              </Button>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <TableRow hover style={styles.rowContainer}>
      {columns?.map((column, index) => (
        <TableCell
          sx={{
            whiteSpace: "nowrap"
          }}
          align={column.align}
          onClick={
            column.id !== "re-activate" ? () => onItemClick(row) : undefined
          }
          key={column.id + "_" + index}
        >
          {column.id === "action" ? (
            <ActionButtons />
          ) : column.id === TIMESHEET_STATUS.submitted ? (
            <TimesheetStatusButtons row={row} />
          ) : (
            <CellDataItem row={row} column={column} />
          )}
        </TableCell>
      ))}

      <TableCell
        sx={{ whiteSpace: "nowrap" }}
        align="center"
        key={"key_edit_delete"}
      >
        {showDelete && renderDelBtn}
        {showDelete &&
          !invoicePage &&
          !weekWiseTimesheetPage &&
          !isActivityLogsPage && (
            <IconButton
              color="default"
              aria-label="edit"
              size="small"
              onClick={() => {
                onItemClick(row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
      </TableCell>
    </TableRow>
  );
}

const useStyles = createStyles((theme) => ({
  rowContainer: {
    cursor: "pointer"
  }
}));
